// Structural guard for UNTRUSTED TSL ASTs — extracted from ast.ts (2026-07-22) so ast.ts
// stays byte-identical to its optimizer-profile-sealed state (the profile hash covers ast.ts;
// this runtime validation concern is not part of the AST type/vocabulary the optimizer depends
// on). Imported by validateTslBlock, createInterpreter, and importStrategy — every boundary where
// a hand-crafted tslAst (tampered share / import / DB row) could reach the recursive evaluator.
//
// The Deno publish-gate mirror (supabase/functions/_shared/blockGate/astGuard.ts) is generated
// from this file by scripts/sync-blockgate-deno.mjs — never hand-edit the copy.
// ── Structural guard for UNTRUSTED ASTs ────────────────────────────────────────
// A TslBlock normally reaches the interpreter through the text parser + validator, which bound size and
// vocabulary. But a hand-crafted `tslAst` JSON (tampered share / import / DB row) can bypass the text
// surface entirely, so every consumer of an untrusted AST (validateTslBlock pre-pass, createInterpreter)
// runs this ITERATIVE guard first. It is deliberately structural, not semantic: it exists to make the
// recursive validator/evaluator safe to run at all (no JS stack blowup, no cycle loop, no unbounded
// node count, no prototype-pollution key names), with limits generous enough that every block the
// semantic validator could ever accept (TSL_LIMITS: 300 nodes / 500 lookback) passes untouched.
export const TSL_STRUCT_LIMITS = {
  maxDepth: 500,          // > any valid AST: the publish budget caps nodes at 300, so depth ≤ 300
  maxNodes: 2000,         // unique nodes (shared subtrees count once — evaluation is memoized per bar)
  maxIdentLen: 64,        // param/state/ctx identifier length
  maxParams: 64,          // structural ceiling (semantic budget is 16)
  maxState: 64,           // structural ceiling (semantic budget is 8)
  maxWindowHard: 10000,   // hard cap on prev/indicator window sizes (semantic budget is 500)
} as const;

/** JS object-machinery names — never allowed as user-controlled object keys / identifier names.
 *  Assigning through them mutates prototype links instead of adding data properties. */
export const isReservedObjectKey = (k: string): boolean =>
  k === '__proto__' || k === 'constructor' || k === 'prototype';

const okIdent = (v: unknown): v is string =>
  typeof v === 'string' && v.length > 0 && v.length <= TSL_STRUCT_LIMITS.maxIdentLen && !isReservedObjectKey(v);

/** Shape-check one untrusted node and return its child slots, or an error string. */
function exprChildren(e: Record<string, unknown>): unknown[] | string {
  switch (e.t) {
    case 'num': return typeof e.v === 'number' ? [] : `'num' node without a numeric v`;
    case 'bool': return typeof e.v === 'boolean' ? [] : `'bool' node without a boolean v`;
    case 'param': case 'state': return okIdent(e.name) ? [] : `'${String(e.t)}' node with a bad name`;
    case 'src': return typeof e.src === 'string' && e.src.length <= 16 ? [] : `'src' node with a bad src`;
    case 'ctx': return okIdent(e.field) ? [] : `'ctx' node with a bad field`;
    case 'indicator':
      return okIdent(e.name) && Array.isArray(e.args) && e.args.length <= 8
        ? (e.args as unknown[]) : `'indicator' node with a bad name/args`;
    case 'prev':
      return typeof e.barsAgo === 'number' && Number.isInteger(e.barsAgo) && e.barsAgo >= 0 && e.barsAgo <= TSL_STRUCT_LIMITS.maxWindowHard
        ? [e.expr] : `prev barsAgo must be a non-negative integer (≤ ${TSL_STRUCT_LIMITS.maxWindowHard})`;
    case 'unop': return [e.a];
    case 'binop': case 'cmp': case 'logic': case 'cross': return [e.a, e.b];
    case 'cond': return [e.cond, e.then, e.else];
    default: return `unknown node type '${String(e.t)}'`;
  }
}

/** Iterative depth/node/cycle/shape check of one untrusted expression graph. `budget.nodes` is shared
 *  across all of a block's expressions so the whole block is bounded. Returns an error string or null.
 *  Cycle-free node SHARING (a DAG) is allowed and counted once — the interpreter memoizes per bar, so
 *  unique-node count is the true per-bar cost — but a node that is its own ancestor is rejected. */
export function checkExprGraph(root: unknown, budget: { nodes: number }): string | null {
  const onPath = new Set<object>();
  const done = new Set<object>();
  type Frame = { node: object; children: unknown[]; next: number };
  const stack: Frame[] = [];
  const enter = (n: unknown): string | null => {
    if (!n || typeof n !== 'object' || Array.isArray(n)) return 'expression node must be an object';
    const o = n as Record<string, unknown> & object;
    if (done.has(o)) return null;                          // shared subtree — already verified
    if (onPath.has(o)) return 'cyclic AST (a node is its own ancestor)';
    if (stack.length >= TSL_STRUCT_LIMITS.maxDepth) return `AST nesting exceeds ${TSL_STRUCT_LIMITS.maxDepth}`;
    if (++budget.nodes > TSL_STRUCT_LIMITS.maxNodes) return `AST exceeds ${TSL_STRUCT_LIMITS.maxNodes} nodes`;
    const c = exprChildren(o);
    if (typeof c === 'string') return c;
    onPath.add(o); stack.push({ node: o, children: c, next: 0 });
    return null;
  };
  let err = enter(root);
  if (err) return err;
  while (stack.length > 0) {
    const f = stack[stack.length - 1];
    if (f.next < f.children.length) { err = enter(f.children[f.next++]); if (err) return err; }
    else { stack.pop(); onPath.delete(f.node); done.add(f.node); }
  }
  return null;
}

/** Structural fail-closed guard over a whole untrusted TslBlock: params/state key hygiene (own-key names,
 *  reserved-name ban, length/count ceilings) + checkExprGraph over every expression body under one shared
 *  node budget. Deterministic, iterative, and generous: any block the semantic validator could accept
 *  passes. Returns an error string, or null when the block is structurally safe to walk/interpret. */
export function checkTslBlockShape(b: unknown): string | null {
  if (!b || typeof b !== 'object' || Array.isArray(b)) return 'TSL block must be an object';
  const blk = b as Record<string, unknown>;
  const checkKeys = (o: unknown, what: string, maxCount: number): string | null => {
    if (o === undefined || o === null) return null;
    if (typeof o !== 'object' || Array.isArray(o)) return `${what} must be an object map`;
    const keys = Object.keys(o);
    if (keys.length > maxCount) return `too many ${what} entries (${keys.length} > ${maxCount})`;
    for (const k of keys) {
      if (isReservedObjectKey(k)) return `reserved name '${k}' cannot be a ${what} key`;
      if (k.length === 0 || k.length > TSL_STRUCT_LIMITS.maxIdentLen) return `${what} key length must be 1..${TSL_STRUCT_LIMITS.maxIdentLen}`;
    }
    return null;
  };
  let err = checkKeys(blk.params, 'params', TSL_STRUCT_LIMITS.maxParams);
  if (err) return err;
  err = checkKeys(blk.state, 'state', TSL_STRUCT_LIMITS.maxState);
  if (err) return err;

  const budget = { nodes: 0 };
  const roots: Array<[string, unknown]> = [];
  if (blk.value !== undefined) roots.push(['value', blk.value]);
  if (blk.signal !== undefined) roots.push(['signal', blk.signal]);
  if (blk.qty !== undefined) roots.push(['qty', blk.qty]);
  if (blk.risk !== undefined && blk.risk !== null) {
    if (typeof blk.risk !== 'object' || Array.isArray(blk.risk)) return 'risk must be an object';
    roots.push(['risk.action', (blk.risk as Record<string, unknown>).action]);
  }
  if (blk.state && typeof blk.state === 'object' && !Array.isArray(blk.state)) {
    for (const [name, s] of Object.entries(blk.state as Record<string, unknown>)) {
      if (!s || typeof s !== 'object' || Array.isArray(s)) return `state '${name}' must be { init, update }`;
      const sv = s as Record<string, unknown>;
      if (typeof sv.init !== 'number') return `state '${name}' init must be a number`;
      roots.push([`state '${name}' update`, sv.update]);
    }
  }
  for (const [label, r] of roots) {
    const e = checkExprGraph(r, budget);
    if (e) return `${label}: ${e}`;
  }
  return null;
}
